import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import type { AssetDepreciationRow, AssetDowntimeRow } from '@his/shared';
import { get, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { cn, dateOnly, dateTime, humanize, money } from '@/lib/utils';
import { minorFromInput } from '@/components/MoneyInput';
import { DataTable, Dialog, FilterField, PageHeader, SearchFilterBar, StatTile, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, Input, Select, StatusBadge, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface Asset {
  id: string;
  code: string;
  name: string;
  category: string;
  manufacturer: string | null;
  serialNo: string | null;
  location: string | null;
  status: string;
  purchaseCostMinor: number;
  warrantyEndsAt: string | null;
  amcEndsAt: string | null;
  warrantyExpired: boolean;
  amcExpired: boolean;
  nextDueAt: string | null;
}

interface Ticket {
  id: string;
  number: string;
  kind: string;
  priority: string;
  status: string;
  description: string;
  resolution: string | null;
  costMinor: number;
  reportedAt: string;
  downtimeMinutes: number;
  asset: { id: string; code: string; name: string; location: string | null };
}

/**
 * Fixed assets and biomedical maintenance (SRS 10.1).
 *
 * Two questions lead, because they are the only two anyone asks of an asset
 * register in a hospital: what is out of service now, and what is overdue for
 * the maintenance that keeps it in service.
 */
export function AssetsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(() => params.get('tab') ?? 'register');

  return (
    <>
      <PageHeader
        title="Assets & maintenance"
        description="Equipment register, preventive schedules and breakdowns"
        actions={
          can('store.manage') &&
          (tab === 'register' ? (
            <Button onClick={() => navigate('/assets/new')}>Add asset</Button>
          ) : tab === 'tickets' ? (
            <Button onClick={() => navigate('/assets/tickets/new')}>Report a breakdown</Button>
          ) : null)
        }
      />
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'register', label: 'Register' },
          { id: 'tickets', label: 'Tickets' },
          { id: 'due', label: 'Maintenance due' },
          { id: 'reports', label: 'Reports' },
        ]}
      />
      {tab === 'register' && <RegisterTab />}
      {tab === 'tickets' && <TicketsTab />}
      {tab === 'due' && <DueTab />}
      {tab === 'reports' && <ReportsTab />}
    </>
  );
}

function RegisterTab() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const assets = useQuery({
    queryKey: ['assets', search, status],
    queryFn: () => get<Asset[]>(`/assets${qs({ q: search || undefined, status: status || undefined, limit: 300 })}`),
  });

  const rows = assets.data ?? [];
  const down = rows.filter((a) => a.status === 'under_repair');

  return (
    <>
      <div className="mb-4 grid gap-3 sm:grid-cols-4">
        <StatTile label="Assets" value={rows.length} />
        <StatTile label="Out of service" value={down.length} tone={down.length ? 'destructive' : 'success'} />
        <StatTile label="Warranty expired" value={rows.filter((a) => a.warrantyExpired).length} tone="warning" />
        <StatTile label="AMC expired" value={rows.filter((a) => a.amcExpired).length} tone="warning" />
      </div>

      <SearchFilterBar
        search={{ value: search, onChange: setSearch, placeholder: 'Name, code or serial number' }}
        filters={
          <FilterField label="Status">
            <Select className="w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {['in_service', 'under_repair', 'standby', 'retired', 'disposed'].map((s) => (
                <option key={s} value={s}>
                  {humanize(s)}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        onReset={
          search || status
            ? () => {
                setSearch('');
                setStatus('');
              }
            : undefined
        }
      />

      <DataTable
        rows={rows}
        loading={assets.isLoading} refreshing={assets.isFetching}
        error={assets.error}
        rowKey={(row) => row.id}
        empty="No assets on the register."
        columns={[
          { header: 'Code', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
          {
            header: 'Asset',
            sort: (row) => row.name,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.name}</p>
                <p className="text-xs text-muted-foreground">
                  {row.manufacturer}
                  {row.serialNo ? ` · ${row.serialNo}` : ''}
                </p>
              </div>
            ),
          },
          { header: 'Location', sort: (row) => row.location, cell: (row) => row.location ?? '—' },
          { header: 'Category', sort: (row) => row.category, cell: (row) => <Badge variant="secondary">{humanize(row.category)}</Badge> },
          {
            header: 'Warranty',
            sort: (row) => row.warrantyEndsAt,
            cell: (row) =>
              row.warrantyEndsAt ? (
                <span className={cn(row.warrantyExpired && 'font-medium text-destructive')}>
                  {dateOnly(row.warrantyEndsAt)}
                </span>
              ) : (
                '—'
              ),
          },
          {
            header: 'Next service',
            sort: (row) => row.nextDueAt,
            cell: (row) =>
              row.nextDueAt ? (
                <span className={cn(new Date(row.nextDueAt) < new Date() && 'font-medium text-destructive')}>
                  {dateOnly(row.nextDueAt)}
                </span>
              ) : (
                <span className="text-muted-foreground">not scheduled</span>
              ),
          },
          { header: 'Status', sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              can('store.manage') && (
                <Button size="sm" variant="outline" onClick={() => navigate(`/assets/schedules/new?assetId=${row.id}`)}>
                  Schedule PM
                </Button>
              ),
          },
        ]}
      />

    </>
  );
}



function TicketsTab() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('open');
  const [resolving, setResolving] = useState<Ticket | null>(null);
  const [resolution, setResolution] = useState('');
  const [cost, setCost] = useState('0');

  const tickets = useQuery({
    queryKey: ['maintenance-tickets', status],
    queryFn: () => get<Ticket[]>(`/assets/tickets${qs({ status: status || undefined, limit: 200 })}`),
  });

  const resolve = useMutation({
    mutationFn: () =>
      put(`/assets/tickets/${resolving?.id}`, {
        status: 'resolved',
        resolution,
        costMinor: minorFromInput(cost),
        returnToService: true,
      }),
    onSuccess: () => {
      toast.success('Ticket resolved', 'The asset is back in service');
      void queryClient.invalidateQueries({ queryKey: ['maintenance-tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
      setResolving(null);
      setResolution('');
      setCost('0');
    },
    onError: (error) => toast.error('Could not resolve the ticket', describeError(error)),
  });

  return (
    <>
      <SearchFilterBar
        filters={
          <FilterField label="Status">
            <Select className="w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              {['open', 'in_progress', 'resolved', 'closed'].map((s) => (
                <option key={s} value={s}>
                  {humanize(s)}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        onReset={status !== 'open' ? () => setStatus('open') : undefined}
      />

      <DataTable
        rows={tickets.data}
        loading={tickets.isLoading} refreshing={tickets.isFetching}
        error={tickets.error}
        rowKey={(row) => row.id}
        empty="Nothing outstanding."
        columns={[
          { header: 'Ticket', sort: (row) => row.number, cell: (row) => <span className="font-mono text-xs">{row.number}</span> },
          {
            header: 'Asset',
            sort: (row) => row.asset.name,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.asset.name}</p>
                <p className="text-xs text-muted-foreground">
                  {row.asset.code}
                  {row.asset.location ? ` · ${row.asset.location}` : ''}
                </p>
              </div>
            ),
          },
          { header: 'Kind', sort: (row) => row.kind, cell: (row) => <Badge variant="secondary">{humanize(row.kind)}</Badge> },
          {
            header: 'Priority',
            sort: (row) => row.priority,
            cell: (row) => (
              <Badge variant={row.priority === 'critical' ? 'destructive' : row.priority === 'high' ? 'warning' : 'muted'}>
                {humanize(row.priority, 'priority')}
              </Badge>
            ),
          },
          { header: 'Reported', sort: (row) => row.reportedAt, cell: (row) => dateTime(row.reportedAt) },
          { header: 'Problem', sort: (row) => row.description, cell: (row) => row.description },
          {
            header: 'Downtime',
            align: 'right',
            sort: (row) => row.downtimeMinutes,
            cell: (row) => (row.downtimeMinutes ? `${Math.round(row.downtimeMinutes / 60)} h` : '—'),
          },
          { header: 'Status', sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              !['resolved', 'closed'].includes(row.status) &&
              can('store.manage') && (
                <Button size="sm" onClick={() => setResolving(row)}>
                  Resolve
                </Button>
              ),
          },
        ]}
      />

      <Dialog
        open={Boolean(resolving)}
        onClose={() => setResolving(null)}
        title="Resolve the ticket"
        description={resolving ? `${resolving.asset.name} · ${resolving.number}` : undefined}
        footer={
          <>
            <Button variant="ghost" onClick={() => setResolving(null)}>
              Cancel
            </Button>
            <Button onClick={() => resolve.mutate()} loading={resolve.isPending} disabled={resolution.trim().length < 3}>
              Resolve
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="What was done?" required>
            <Textarea rows={3} value={resolution} onChange={(e) => setResolution(e.target.value)} />
          </Field>
          <Field label="Cost">
            <Input type="text" dir="ltr" inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} />
          </Field>
        </div>
      </Dialog>
    </>
  );
}


function DueTab() {
  const due = useQuery({
    queryKey: ['maintenance-due'],
    queryFn: () =>
      get<
        {
          id: string;
          kind: string;
          nextDueAt: string;
          lastDoneAt: string | null;
          daysUntilDue: number;
          overdue: boolean;
          asset: { id: string; code: string; name: string; location: string | null; status: string };
        }[]
      >('/assets/alerts/maintenance-due?withinDays=90'),
  });

  const overdue = due.data?.filter((d) => d.overdue) ?? [];

  return (
    <>
      {overdue.length > 0 && (
        <Alert variant="destructive" className="mb-4">
          <span className="inline-flex items-center gap-1 font-semibold">
            <AlertTriangle className="h-4 w-4" /> {overdue.length} machine{overdue.length > 1 ? 's are' : ' is'} overdue
            for maintenance
          </span>
        </Alert>
      )}

      <DataTable
        rows={due.data}
        loading={due.isLoading} refreshing={due.isFetching}
        error={due.error}
        rowKey={(row) => row.id}
        empty="Nothing due in the next 90 days."
        columns={[
          {
            header: 'Asset',
            sort: (row) => row.asset.name,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.asset.name}</p>
                <p className="text-xs text-muted-foreground">
                  {row.asset.code}
                  {row.asset.location ? ` · ${row.asset.location}` : ''}
                </p>
              </div>
            ),
          },
          { header: 'Kind', sort: (row) => row.kind, cell: (row) => <Badge variant="secondary">{humanize(row.kind)}</Badge> },
          { header: 'Last done', sort: (row) => row.lastDoneAt, cell: (row) => (row.lastDoneAt ? dateOnly(row.lastDoneAt) : 'never') },
          { header: 'Due', sort: (row) => row.nextDueAt, cell: (row) => dateOnly(row.nextDueAt) },
          {
            header: 'In',
            align: 'right',
            sort: (row) => row.daysUntilDue,
            cell: (row) => (
              <span className={cn('tabular', row.overdue && 'font-semibold text-destructive')}>
                {row.overdue ? `${Math.abs(row.daysUntilDue)} d late` : `${row.daysUntilDue} d`}
              </span>
            ),
          },
          { header: 'Asset status', sort: (row) => row.asset.status, cell: (row) => <StatusBadge status={row.asset.status} /> },
        ]}
      />
    </>
  );
}

function ReportsTab() {
  const from = new Date(Date.now() - 90 * 86_400_000).toISOString();
  const to = new Date().toISOString();

  const depreciation = useQuery({
    queryKey: ['asset-depreciation'],
    queryFn: () => get<AssetDepreciationRow[]>('/assets/reports/depreciation'),
  });

  const downtime = useQuery({
    queryKey: ['asset-downtime'],
    queryFn: () => get<AssetDowntimeRow[]>(`/assets/reports/downtime?from=${from}&to=${to}`),
  });

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-sm font-semibold">Depreciation and book value</h3>
        <DataTable
          rows={depreciation.data}
          loading={depreciation.isLoading} refreshing={depreciation.isFetching}
          error={depreciation.error}
          rowKey={(row) => row.assetId}
          empty="No depreciable assets."
          columns={[
            { header: 'Asset', sort: (row) => row.code, cell: (row) => `${row.code} · ${row.name}` },
            { header: 'Purchased', sort: (row) => row.purchasedAt, cell: (row) => (row.purchasedAt ? dateOnly(row.purchasedAt) : '—') },
            { header: 'Age', align: 'right', sort: (row) => row.ageYears, cell: (row) => `${row.ageYears} yr` },
            { header: 'Cost', align: 'right', sort: (row) => row.purchaseCostMinor, cell: (row) => money(row.purchaseCostMinor) },
            { header: 'Accumulated', align: 'right', sort: (row) => row.accumulatedMinor, cell: (row) => money(row.accumulatedMinor) },
            { header: 'Book value', align: 'right', sort: (row) => row.bookValueMinor, cell: (row) => <span className="font-medium">{money(row.bookValueMinor)}</span> },
          ]}
        />
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold">Downtime, last 90 days</h3>
        <DataTable
          rows={downtime.data}
          loading={downtime.isLoading} refreshing={downtime.isFetching}
          error={downtime.error}
          rowKey={(row) => row.assetId}
          empty="Nothing has broken down."
          columns={[
            { header: 'Asset', sort: (row) => row.code, cell: (row) => `${row.code} · ${row.name}` },
            { header: 'Tickets', align: 'right', sort: (row) => row.tickets, cell: (row) => row.tickets },
            { header: 'Downtime', align: 'right', sort: (row) => row.downtimeMinutes, cell: (row) => `${Math.round(row.downtimeMinutes / 60)} h` },
            { header: 'Repair cost', align: 'right', sort: (row) => row.costMinor, cell: (row) => money(row.costMinor) },
          ]}
        />
      </div>
    </div>
  );
}
